import IconLoader from "@/features/Common/components/IconLoader";
import theme, { panelStyle } from "@/theme";
import { Stack, Typography } from "@mui/material";
import {
  WellnessCategoryCardProps,
  WellnessScoreStatus,
} from "../Common/types";
import { formatDateToISO } from "../Common/helpers";
import { useTranslation } from "react-i18next";

export default function WellnessCategoryCard(props: WellnessCategoryCardProps) {
  const { icon, title, status, description, lastUpdated, children } = props;

  const { t } = useTranslation();

  const getStatusLabel = (status: WellnessScoreStatus | undefined) => {
    switch (status) {
      case WellnessScoreStatus.Healthy:
        return (
          <Stack
            sx={{
              backgroundColor: "accent1.main",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="primary.contrastText"
            >
              {t("WellnessStatus.healthy")}
            </Typography>
          </Stack>
        );
      case WellnessScoreStatus.NeedsAttention:
        return (
          <Stack
            sx={{
              backgroundColor: "secondary.700",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="neutral.900"
            >
              {t("WellnessStatus.needsAttention")}
            </Typography>
          </Stack>
        );
      case WellnessScoreStatus.NeedsSupport:
        return (
          <Stack
            sx={{
              backgroundColor: "error.main",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="primary.contrastText"
            >
              {t("WellnessStatus.needsSupport")}
            </Typography>
          </Stack>
        );
      default:
        return (
          <Stack
            sx={{
              backgroundColor: "neutral.500",
              borderRadius: 1,
              alignSelf: "start",
            }}
            padding={1}
          >
            <Typography
              fontWeight={600}
              fontSize={12}
              variant="body2"
              color="primary.contrastText"
            >
              {t("WellnessStatus.incomplete")}
            </Typography>
          </Stack>
        );
    }
  };

  return (
    <>
      <Stack sx={{ ...panelStyle }} width="100%">
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack flexDirection="row" alignItems="center" gap={1}>
            {icon}
            <Typography
              fontWeight={600}
              variant="h3"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: 16,
                lineHeight: 1.25,
                letterSpacing: "-0.03em",
              }}
            >
              {title}
            </Typography>
          </Stack>
          {getStatusLabel(status)}
        </Stack>
        {lastUpdated && (
          <Stack mt={2}>
            <Typography variant="body2" fontWeight={600}>
              Last updated:{" "}
              <span style={{ color: theme.palette.neutral[500] }}>
                {formatDateToISO(new Date(lastUpdated))}
              </span>
            </Typography>
          </Stack>
        )}
        <Stack mt={1}>
          <Typography
            sx={{
              fontSize: 14,
              lineHeight: 1.5,
              color: "neutral.700",
            }}
          >
            {description}
          </Typography>
        </Stack>
        <Stack alignItems="center" gap={2} mt={2} width="100%">
          {children}
        </Stack>
      </Stack>
    </>
  );
}
